/** Service Desk console: Contracts list and drawer (C08, SD-S3-10). */
export const serviceContracts = {
  title: "Contracts",
  subtitle: "Service plans, the SLA tier they apply and the entitlements left. A ticket for a covered asset picks up the contract SLA.",
  orgTab: { emptyTitle: "No service contract", emptyBody: "This organisation uses the workspace default SLA until you write a contract for it." },
  actions: { create: "New contract" },
  kind: { service: "Service", maintenance: "Maintenance", support: "Support", warranty_extension: "Warranty extension" },
  phase: {
    draft: "Draft",
    upcoming: "Starts {{start}}",
    active: "Active until {{date}}",
    expiring_one: "Ends in {{count}} day",
    expiring_other: "Ends in {{count}} days",
    expired: "Expired {{date}}",
    cancelled: "Cancelled",
  },
  stats: {
    label: "Summary",
    active: "In force",
    activeHint: "contracts covering things today",
    expiring: "Expiring soon",
    expiringHint: "ending within 60 days",
    expired: "Expired",
    expiredHint: "not renewed",
    draft: "Drafts",
    draftHint: "not yet activated",
  },
  filters: {
    group: "Contract status filter",
    chip: { all: "All", active: "In force", expiring: "Expiring soon", expired: "Expired", draft: "Drafts" },
    search: "Search number or name",
    searchLabel: "Search contracts",
    kind: "Contract type",
    allKinds: "All types",
  },
  list: {
    label: "Contracts",
    contract: "Contract",
    organisation: "Organisation",
    sla: "SLA tier",
    ends: "Ends",
    entitlements: "Entitlements",
    assets: "Assets",
    fee: "Fee",
    noEntitlements: "None set",
    allAssets: "All assets",
    showing: "Showing {{shown}} of {{total}} contracts",
  },
  empty: {
    title: "No contracts yet",
    body: "Write a service plan to give a customer an SLA tier and a set of entitlements such as maintenance visits.",
    filteredTitle: "No contracts match",
    filteredBody: "Clear a filter or search for another contract.",
  },
  error: { title: "Contracts couldn't load", body: "Check your connection and try again. Nothing was changed." },
  coverage: {
    labor: "Labour",
    travel: "Travel",
    parts: "Parts",
    covered: "covered",
    notCovered: "not covered",
    exclusions: "Not included: {{text}}",
  },
  entitlement: {
    kind: {
      pm_visits: "Preventive maintenance visits",
      calibrations: "Calibrations",
      onsite_visits: "On-site visits",
      remote_support_hours: "Remote support hours",
      ticket_allowance: "Tickets",
    },
    period: { contract: "per contract", year: "per contract year", month: "per month" },
    readout: "{{used}} of {{quantity}} used",
    barLabel: "{{kind}}: {{used}} of {{quantity}} used",
    remaining_one: "{{value}} remaining",
    remaining_other: "{{value}} remaining",
    over_one: "{{value}} over the allowance",
    over_other: "{{value}} over the allowance",
    exhausted: "Fully used",
  },
  banner: {
    expiring_one: "Ends in {{count}} day.",
    expiring_other: "Ends in {{count}} days.",
    expiringBody: "Agree the renewal with the customer before its assets fall back to the workspace default SLA.",
    expired: "This contract expired on {{date}}. Its assets fall back to the workspace default SLA.",
    cancelled: "This contract was cancelled.",
    cancelledReason: "This contract was cancelled: {{reason}}",
    draft: "This contract is a draft. Activate it to apply its SLA and count entitlements.",
    exhausted: "An entitlement is fully used. A further visit is beyond the contract and is flagged chargeable.",
  },
  drawer: {
    loading: "Loading the contract",
    notFoundTitle: "Contract not found",
    notFoundBody: "It may have been removed. Go back to the list to find it.",
    organisation: "Organisation",
    term: "Term",
    termValue: "{{start}} to {{end}}",
    fee: "Fee",
    billing: "Billing reference",
    renewal: "Renewal reminder",
    renewalValue_one: "{{count}} day before the end",
    renewalValue_other: "{{count}} days before the end",
    slaChip: "SLA {{name}}",
    entitlements: "Entitlements",
    noEntitlements: "No entitlements are set. Edit the contract to add some.",
    coverage: "Coverage",
    assets: "Covered assets",
    assetsCount_one: "Covered assets ({{count}})",
    assetsCount_other: "Covered assets ({{count}})",
    everyAsset: "Every asset of the organisation, now and later.",
    noAssets: "No assets are covered yet.",
    usage: "Usage log",
    terms: "Terms",
    edit: "Edit contract",
    activate: "Activate",
    activated: "{{number}} is active",
    cancel: "Cancel contract",
    cancelTitle: "Cancel {{number}}?",
    cancelBody: "Its assets stop being covered at once. Entitlements already counted stay in the usage log.",
    cancelConfirm: "Cancel contract",
    cancelled: "{{number}} cancelled",
  },
  consume: {
    open: "Count usage",
    title: "Count usage",
    description: "Counts an amount against {{number}}.",
    entitlement: "Entitlement",
    amount: "Amount",
    note: "Note",
    count: "Count",
    counted: "Usage counted",
    waived: "Left uncounted",
    pastAllowance: "That is past the allowance. Nothing was counted.",
    reason: "Reason",
    reasonHint: "At least {{min}} characters. It is saved in the usage log and the audit trail.",
    countAnyway: "Count anyway",
    waive: "Don't count",
    problems: { amount: "Enter an amount above zero with at most two decimals.", reason: "Give a reason of at least {{min}} characters." },
  },
  usage: {
    label: "Usage",
    reversed: "Usage reversed",
    reversedChip: "Reversed",
    notCounted: "Not counted",
    chargeable: "Chargeable",
    over: "Past the allowance",
    reverse: "Reverse",
    reason: "Reason: {{reason}}",
    actor: { staff: "Staff", contact: "Client", system: "System", connector: "Connector", import: "Import", visit: "Visit", automation: "Automation" },
    emptyTitle: "No usage yet",
    emptyBody: "Visits and tickets counted against this contract appear here.",
    errorTitle: "The usage log couldn't load",
    errorBody: "Check your connection and try again.",
  },
  form: {
    createTitle: "New contract",
    createDescription: "It starts as a draft until you activate it.",
    editTitle: "Edit {{number}}",
    organisation: "Organisation",
    chooseOrganisation: "Choose an organisation",
    name: "Contract name",
    kind: "Type",
    startsOn: "Starts on",
    endsOn: "Ends on",
    sla: "SLA tier",
    slaHint: "Applied to tickets for the covered assets.",
    noSla: "Workspace default",
    coverage: "What it covers",
    exclusions: "Not included",
    entitlements: "Entitlements",
    entitlementsHint: "Tick what the contract includes and how many. Leave a kind unticked for none.",
    quantityOf: "Quantity of {{kind}}",
    periodOf: "Period of {{kind}}",
    assets: "Covered assets",
    allAssets: "Every asset of the organisation",
    allAssetsHint: "New assets of this organisation are covered too.",
    pickOrganisationFirst: "Choose the organisation first.",
    noAssets: "This organisation has no assets yet.",
    price: "Fee",
    priceHint: "Staff only, excluding VAT.",
    billingRef: "Billing reference",
    renewalDays: "Renewal reminder (days)",
    renewalHint: "How long before the end to remind you.",
    clientVisiblePrice: "Show the fee to the client",
    terms: "Terms",
    create: "Create contract",
    save: "Save changes",
    created: "{{number}} created",
    updated: "{{number}} saved",
  },
  validation: {
    organisation: "Choose the organisation this contract is for.",
    name: "Give the contract a name of up to {{max}} characters.",
    required: "Choose a date.",
    dates: "The contract must end on or after the day it starts.",
    price: "Enter an amount of zero or more.",
    renewalDays: "Enter a whole number of days from 0 to {{max}}.",
    entitlement: "Give each ticked entitlement a quantity of zero or more.",
    assets: "Pick at least one asset, or cover every asset of the organisation.",
  },
} as const;
